import { html } from 'lit-element';
import {
  defaultCellFontSize,
  defaultFontColor,
  bubbleStyles,
  responseStyles,
} from '../cssUtils.js';
import { getIcon, renderBubble } from '../utils.js';
import { CustomLitElement } from '../CustomLitElement.js';
import {
  defineElement,
  importMathRendering,
} from '../classroom-tool-extensions/classifier/utils.js';
import { PieMathRenderer } from '../classroom-tool-extensions/common/PieMathRenderer.js';

/**
 * @element match-response
 * @prop {Object} model - The model for the pie
 * @prop {Object} session - The response session
 * @cssprop --pie-live-session-background-color - change the background color. Should be used only if there's no imageUrl
 * @cssprop --pie-live-session-text-color - change the text color
 * @cssprop --pie-live-session-bubble-text-color - change the text color
 * @cssprop --pie-live-session-font-family - change the font family
 * @cssprop --pie-live-session-font-size-small - change the font size
 * @cssprop --pie-live-session-response-height - change the maximum height
 * @cssprop --pie-live-session-response-width - change the maximum width
 */
export class MatchResponse extends CustomLitElement {
  static get properties() {
    return {
      model: { type: Object, attribute: false },
      session: { type: Array, attribute: false },
      hoveredChoice: { type: Object },
    };
  }

  set hoveredChoice(val) {
    const oldVal = this._hoveredChoice;

    if (!oldVal || !val || oldVal.value !== val.value) {
      this._hoveredChoice = val;
      this.requestUpdate('hoveredChoice', oldVal);
    }
  }

  get hoveredChoice() {
    return this._hoveredChoice;
  }

  constructor() {
    super();
    this._hoveredChoice = null;
  }

  createRenderRoot() {
    return this;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    defineElement('math-rendering-element', PieMathRenderer);

    if (!this.mathRenderingModuleUrlImported) {
      importMathRendering();

      this.mathRenderingModuleUrlImported = true;
    }
  }

  mouseover(e, value) {
    const item = e.target.closest('.choice-value');

    if (item && item.getBoundingClientRect) {
      const bounds = item.getBoundingClientRect();

      this.hoveredChoice = {
        value,
        top: bounds.top + bounds.height / 2,
        left: bounds.left + bounds.width,
      };
    }
  }

  mouseout() {
    this.hoveredChoice = null;
  }

  _renderResponse() {
    const { headers, rows, choiceMode } = this.model || {};
    const { answers } = this.session || {};

    if (!headers || !rows) return html``;

    return html`<table>
      <tr>
        ${headers.map(
          (header, index) => html`<td>${index ? `C${index}` : 'Q-id'}</td>`
        )}
      </tr>

      ${rows.map(row => {
        const { values: correctValues, title } = row;
        const rowAnswers = answers ? answers[row.id] : [];

        return html` <tr
          class="choice-value"
          @mouseover=${e => this.mouseover(e, title)}
          @mouseout=${this.mouseout}
        >
          <td>${row.id}</td>

          ${(correctValues || rowAnswers).map((col, index) => {
            let correctnessIndicator = '';
            const userAnswer = rowAnswers && rowAnswers[index];

            if (userAnswer) {
              correctnessIndicator = getIcon(correctValues[index]);
            }

            return html`
              <td>
                <div class="choice ${choiceMode} is-${userAnswer}">
                  ${correctnessIndicator}
                </div>
              </td>
            `;
          })}
        </tr>`;
      })}
    </table>`;
  }

  render() {
    return html`
      <style>
        ${bubbleStyles} ${responseStyles} .match.response {
          font-size: var(
            --pie-live-session-font-size-small,
            ${defaultCellFontSize}px
          );
          padding: 4px 8px;
        }
        .match .response-content {
          overflow: auto;
          width: 100%;
        }
        .match p {
          margin: 0;
        }
        .match .choice {
          border: 1px solid
            var(--pie-live-session-text-color, ${defaultFontColor});
          height: 12px;
          width: 12px;
          font-size: var(
            --pie-live-session-font-size-small,
            ${defaultCellFontSize}px
          );
          text-align: center;
          color: white;
        }
        .match .checkbox {
        }
        .match .radio {
          border-radius: 7px;
        }
        .match .is-true {
          background-color: var(
            --pie-live-session-text-color,
            ${defaultFontColor}
          );
        }
        .match .choice-value {
          cursor: pointer;
          display: table-row;
        }
        .match table {
          table-layout: fixed;
          width: 100%;
          border: none !important;
        }

        .match table td {
          border: none !important;
          height: unset !important;
          width: unset !important;
        }
      </style>
      <div class="response match">
        <div class="response-content">${this._renderResponse()}</div>
        ${renderBubble(this.hoveredChoice)}
      </div>
    `;
  }
}

export default MatchResponse;
